import imageCompression from "browser-image-compression";

// Applicants upload straight from a phone, where a photo is routinely 3-6 MB.
// Tutor registration and job placement compress on the client before Storage
// sees the file. These ceilings are what the compressed result must fit
// inside, and storage.rules rejects anything larger.
//
// A headshot does not need more than 800px. An ID photo needs more pixels so
// the name and number stay readable, but it is still stored as JPEG rather
// than the original multi-megabyte PNG or camera file. PDFs cannot be
// recompressed here, so an oversized one is refused.
export const PHOTO_MAX_BYTES = 300 * 1024;
export const DOCUMENT_MAX_BYTES = 1024 * 1024;

export const APPLICANT_PHOTO_MAX_BYTES = 120 * 1024;
export const APPLICANT_ID_MAX_BYTES = 400 * 1024;
export const APPLICANT_PDF_MAX_BYTES = 800 * 1024;

export const IMAGE_TYPES = ["image/png", "image/jpg", "image/jpeg", "image/webp"];
export const DOCUMENT_TYPES = [...IMAGE_TYPES, "application/pdf"];

export const isImageFile = (file) => Boolean(file) && IMAGE_TYPES.includes(file.type);

export const formatBytes = (bytes) => {
  if (bytes >= 1024 * 1024) return `${Math.round((bytes / (1024 * 1024)) * 10) / 10}MB`;
  return `${Math.round(bytes / 1024)}KB`;
};

const jpegName = (name) => `${(name || "upload").replace(/\.[^.]+$/, "") || "upload"}.jpg`;

/**
 * Shrink an image to fit `maxBytes`. Non-images (PDFs) are returned untouched
 * because they cannot be compressed in the browser -- the caller is expected
 * to reject an oversized one.
 *
 * `asJpeg` re-encodes PNG and WebP uploads. A phone screenshot of an Aadhaar
 * is often a multi-megabyte PNG; JPEG at the same dimensions is a fraction of
 * that. A JPEG that is already under the ceiling is left alone.
 */
export const compressImage = async (file, { maxBytes, maxWidthOrHeight, asJpeg = false }) => {
  if (!isImageFile(file)) return file;
  if (file.type === "image/jpeg" && file.size <= maxBytes) return file;

  const compressed = await imageCompression(file, {
    maxSizeMB: maxBytes / (1024 * 1024),
    maxWidthOrHeight,
    useWebWorker: true,
    initialQuality: asJpeg ? 0.75 : 0.8,
    ...(asJpeg ? { fileType: "image/jpeg" } : {}),
  });

  if (!asJpeg) return compressed;

  return new File([compressed], jpegName(file.name), {
    type: "image/jpeg",
    lastModified: Date.now(),
  });
};

export const compressPhoto = (file) =>
  compressImage(file, { maxBytes: PHOTO_MAX_BYTES, maxWidthOrHeight: 1000 });

// Documents keep more resolution than photos so small print stays legible.
export const compressDocument = (file) =>
  compressImage(file, { maxBytes: DOCUMENT_MAX_BYTES, maxWidthOrHeight: 2000 });

export const compressApplicantPhoto = (file) =>
  compressImage(file, {
    maxBytes: APPLICANT_PHOTO_MAX_BYTES,
    maxWidthOrHeight: 800,
    asJpeg: true,
  });

export const compressApplicantId = (file) =>
  compressImage(file, {
    maxBytes: APPLICANT_ID_MAX_BYTES,
    maxWidthOrHeight: 1600,
    asJpeg: true,
  });

export const applicantIdLimit = (file) =>
  file?.type === "application/pdf" ? APPLICANT_PDF_MAX_BYTES : APPLICANT_ID_MAX_BYTES;
